import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Loader2, ChevronUp, ChevronDown, Lock, Radio, Film } from "lucide-react";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { RadioListeners, useRadioHeartbeat } from "@/components/RadioListeners";
import { RwandaTicker } from "@/components/football/Rwanda";
import { useResilientAudio } from "@/components/useResilientAudio";
import { usePreviewGate, PreviewPill } from "@/components/PreviewGate";

const Equalizer = ({ active }) => (
  <div className="flex items-end gap-[2px] h-5 w-6">
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className={`eq-bar w-1 rounded-sm ${active ? "bg-[#E11D2A]" : "bg-slate-600"}`}
        style={{ animationPlayState: active ? "running" : "paused", animationDelay: `${i * 0.15}s`, height: "40%" }} />
    ))}
  </div>
);

export const PersistentRadioPlayer = () => {
  const { nowPlaying, isPlaying, stop, streamUrl, fallbacks, refreshStream, requestPlay, expirePreview } = usePlayer();
  const { isSubscribed, isAuthed } = useAuth();
  const audioRef = useRef(null);
  useRadioHeartbeat(isPlaying && !!streamUrl);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const nav = useNavigate();

  useEffect(() => { const a = audioRef.current; if (a) a.volume = muted ? 0 : volume; }, [volume, muted]);
  const { status } = useResilientAudio({ audioRef, playing: isPlaying, urls: [streamUrl, ...(fallbacks || [])].filter(Boolean),
    onFatal: refreshStream, onUserPause: stop, meta: nowPlaying, setLoading });

  const preview = usePreviewGate("radio", isPlaying && !isSubscribed, () => { expirePreview(); toast.error("Your free preview has ended — subscribe to keep listening"); });
  const handleToggle = () => requestPlay();

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-[#273042] shadow-2xl" data-testid="persistent-radio-player">
      {isPlaying && !isSubscribed && (
        <div className="absolute -top-11 left-1/2 -translate-x-1/2 max-w-[94vw]"><PreviewPill st={preview} label="Radio" testid="radio-preview-pill" /></div>
      )}
      <audio ref={audioRef} preload="none" playsInline data-testid="radio-audio" />
      <RwandaTicker />
      {expanded && (
        <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-5 border-b border-[#273042] flex items-center gap-4">
          <img src={nowPlaying?.coverImage || "/bb-logo.png"} alt="" className="w-16 h-16 rounded-xl object-cover border border-[#273042]" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-[#E11D2A] font-bold flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] live-dot" /> On Air Now</div>
            <div className="text-white font-bold truncate mt-0.5">{nowPlaying?.showTitle || "B&B KIGALI Live"}</div>
            <div className="text-sm text-slate-400 truncate">{nowPlaying?.djName || "89.7 FM"}</div>
            <RadioListeners className="text-slate-300 mt-1" testid="player-listeners-expanded" />
          </div>
        </div>
      )}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto h-[68px] flex items-center gap-3 sm:gap-4">
        <button onClick={handleToggle} data-testid="live-radio-play-btn" aria-label={isPlaying ? "Pause live radio" : "Play live radio"}
          className="w-11 h-11 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white flex items-center justify-center btn-glow transition-colors shrink-0">
          {loading && isPlaying ? <Loader2 className="w-5 h-5 animate-spin" /> : isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>

        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Equalizer active={isPlaying} />
          <div className="min-w-0">
            <div className="text-white text-sm font-semibold truncate flex items-center gap-1.5">
              <Radio className="w-3.5 h-3.5 text-[#E11D2A] shrink-0" />
              {nowPlaying?.showTitle || "B&B KIGALI 89.7"}
            </div>
            <div className="text-xs text-slate-400 truncate flex items-center gap-2"><span data-testid="radio-status">{isPlaying ? (status === "reconnecting" ? "Reconnecting…" : status === "offline" ? "Waiting for network…" : "Live · 89.7 FM") : (nowPlaying?.djName || "Tap play to listen live")}</span><RadioListeners className="text-slate-300 hidden sm:inline-flex" testid="player-listeners" /></div>
          </div>
        </div>

        <button onClick={() => nav("/highlights")} data-testid="player-highlights-btn" aria-label="Highlights videos"
          className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#E11D2A]/60 text-white hover:bg-[#E11D2A]/15 text-xs font-extrabold uppercase shrink-0 transition-colors">
          <Film className="w-3.5 h-3.5 text-[#E11D2A]" /> <span className="hidden sm:inline">Highlights</span>
        </button>

        {!isSubscribed && (
          <button onClick={() => { if (!isAuthed) window.dispatchEvent(new Event("open-auth")); else window.dispatchEvent(new CustomEvent("open-subscribe")); }} data-testid="player-unlock-btn"
            className="flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#E11D2A] hover:bg-[#C8121F] text-white text-xs font-extrabold uppercase btn-glow shrink-0">
            <Lock className="w-3.5 h-3.5" /> Unlock
          </button>
        )}

        <div className="hidden md:flex items-center gap-2 w-36">
          <button onClick={() => setMuted((m) => !m)} data-testid="player-mute-btn" aria-label={muted || volume === 0 ? "Unmute" : "Mute"} className="text-slate-400 hover:text-white">
            {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <input type="range" min="0" max="1" step="0.01" value={muted ? 0 : volume}
            onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
            data-testid="player-volume-slider" aria-label="Volume"
            className="flex-1 h-1 accent-[#E11D2A] cursor-pointer" />
        </div>

        <button onClick={() => setExpanded((e) => !e)} data-testid="player-expand-btn" aria-label={expanded ? "Collapse player" : "Expand player"} aria-expanded={expanded} className="text-slate-400 hover:text-white w-9 h-9 flex items-center justify-center">
          {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};
